import { type ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Card, Display, Text, cn, useRebrand } from '@growtherapy/sprout-ui';

import PreSessionImage from '../../../assets/images/pre-session-image.svg?react';
import { VisitorDrawerState } from '../../drawerState';

export interface PreSessionPromptProps {
  callToActionContent: ReactNode;
  hasOutstandingMeasures: boolean;
}

export type CTAProps = {
  openDrawer: (drawer: VisitorDrawerState) => void;
  variant?: PreSessionPromptVariant;
};

export enum PreSessionPromptVariant {
  CLIENT_MEASURES = 'CLIENT_MEASURES',
  REFLECTION_TOPICS = 'REFLECTION_TOPICS',
  VIBES = 'VIBES',
}

function BodyContent({ hasOutstandingMeasures }: { hasOutstandingMeasures: boolean }) {
  const t = useTranslate();
  if (hasOutstandingMeasures) {
    return (
      <span>
        {t('telehealth-core:waitingRoom.yourProviderWillStartYourSessionSoon')}{' '}
        {t('telehealth-core:waitingRoom.youCanFinishYourHealthCheckInFormsNowOrAfterTheSessionStarts')}
      </span>
    );
  }
  return (
    <span>
      {t('telehealth-core:waitingRoom.youCanJoinWhenYourProviderIsReady')}
      <br />
      {t('telehealth-core:waitingRoom.theyHave10MinGracePeriod')}
    </span>
  );
}

export function PreSessionPrompt({ callToActionContent, hasOutstandingMeasures }: PreSessionPromptProps) {
  const t = useTranslate();
  const isRebrand = useRebrand();
  const { headingContent, bodyContent } = {
    headingContent: t('telehealth-core:waitingRoom.youreInTheRightPlace'),
    bodyContent: <BodyContent hasOutstandingMeasures={hasOutstandingMeasures} />,
  };

  return (
    <Card className='flex flex-col mobile:border-x rounded-xl items-center max-w-[38rem] gap-y-10 sm:p-10 rebrand:border-border-default rebrand:bg-surface-inverse'>
      {!isRebrand && (
        <div className='hidden sm:block'>
          <PreSessionImage
            aria-label={t('telehealth-core:waitingRoom.aLightPurpleSketchOfAPairOfHandsClaspedTogether')}
          />
        </div>
      )}

      <div className='flex flex-col gap-y-4 items-center'>
        {isRebrand ? (
          <Display.Small className='rebrand:text-text-inverse'>{headingContent}</Display.Small>
        ) : (
          <Text as='h2' variant='lg' className='font-medium'>
            {headingContent}
          </Text>
        )}

        <Body.Default className={cn('text-neutral-700 text-center rebrand:text-text-inverse max-w-[30rem]')}>
          {bodyContent}
        </Body.Default>

        {callToActionContent}
      </div>
    </Card>
  );
}
